import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Eye, EyeOff, AlertTriangle, Key, Upload, Download, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../auth/authStore';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  status: 'active' | 'invited' | 'disabled';
  createdAt: string;
  lastLoginAt?: string;
}

interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  password?: string;
  sendInvitation: boolean;  // Always send notification
}

interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  roles?: string[];
  status?: 'active' | 'invited' | 'disabled';
}

// Available roles for firm users
const USER_ROLES = [
  { value: 'Manager', label: 'Manager', description: 'Can manage users and access all features' },
  { value: 'Staff', label: 'Staff', description: 'Standard user with limited permissions' },
  { value: 'Client', label: 'Client', description: 'Client access with restricted features' }
];

// User status options
const USER_STATUSES = [
  { value: 'active', label: 'Active', color: 'green' },
  { value: 'invited', label: 'Invited', color: 'yellow' },
  { value: 'disabled', label: 'Disabled', color: 'red' }
];

// Validation schemas
const createUserSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  firstName: z.string().min(1, 'First name is required').max(50, 'First name must be less than 50 characters'),
  lastName: z.string().min(1, 'Last name is required').max(50, 'Last name must be less than 50 characters'),
  roles: z.array(z.string()).min(1, 'Please select at least one role'),
  password: z.string().optional(),
  sendInvitation: z.boolean()
});

const updateUserSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50, 'First name must be less than 50 characters'),
  lastName: z.string().min(1, 'Last name is required').max(50, 'Last name must be less than 50 characters'),
  roles: z.array(z.string()).min(1, 'Please select at least one role'),
  status: z.enum(['active', 'invited', 'disabled'])
});

type CreateUserFormData = z.infer<typeof createUserSchema>;
type UpdateUserFormData = z.infer<typeof updateUserSchema>;

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userData: CreateUserData) => void;
  loading: boolean;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [useCustomPassword, setUseCustomPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      roles: [],
      password: undefined,
      sendInvitation: true
    }
  });

  const watchedRoles = watch('roles');

  const handleClose = () => {
    reset();
    setShowPassword(false);
    setUseCustomPassword(false);
    onClose();
  };

  const onFormSubmit = (data: CreateUserFormData) => {
    const submitData = {
      ...data,
      password: useCustomPassword ? data.password : undefined,
      sendInvitation: true  // Always send notification email
    };
    onSubmit(submitData);
  };

  const handleRoleToggle = (roleValue: string) => {
    const currentRoles = watchedRoles || [];
    const newRoles = currentRoles.includes(roleValue)
      ? currentRoles.filter(r => r !== roleValue)
      : [...currentRoles, roleValue];
    setValue('roles', newRoles);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add New User</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              {...register('email')}
              type="email"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loading}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* First Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register('firstName')}
              type="text"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.firstName ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loading}
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register('lastName')}
              type="text"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.lastName ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loading}
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>
            )}
          </div>

          {/* Roles */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Roles <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {USER_ROLES.map((role) => (
                <label key={role.value} className="flex items-start">
                  <input
                    type="checkbox"
                    checked={watchedRoles?.includes(role.value) || false}
                    onChange={() => handleRoleToggle(role.value)}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    disabled={loading}
                  />
                  <div className="ml-2">
                    <div className="text-sm font-medium text-gray-900">{role.label}</div>
                    <div className="text-xs text-gray-500">{role.description}</div>
                  </div>
                </label>
              ))}
            </div>
            {errors.roles && (
              <p className="text-red-500 text-sm mt-1">{errors.roles.message}</p>
            )}
          </div>

          {/* User Setup Method - Radio Buttons */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              How should the user set up their account? <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              {/* Option 1: Send Invitation Email */}
              <label className="flex items-start p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary-300 transition-colors" style={{borderColor: !useCustomPassword ? '#2563eb' : '#e5e7eb'}}>
                <input
                  type="radio"
                  name="setupMethod"
                  checked={!useCustomPassword}
                  onChange={() => {
                    setUseCustomPassword(false);
                    setValue('password', '');
                  }}
                  className="mt-1 w-4 h-4 text-primary-600 focus:ring-primary-500 cursor-pointer"
                  disabled={loading}
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">Send Invitation Email</div>
                  <div className="text-xs text-gray-600 mt-1">
                    User will receive an invitation email with a link to set their own password.
                  </div>
                </div>
              </label>

              {/* Option 2: Set Custom Password */}
              <label className="flex items-start p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary-300 transition-colors" style={{borderColor: useCustomPassword ? '#2563eb' : '#e5e7eb'}}>
                <input
                  type="radio"
                  name="setupMethod"
                  checked={useCustomPassword}
                  onChange={() => setUseCustomPassword(true)}
                  className="mt-1 w-4 h-4 text-primary-600 focus:ring-primary-500 cursor-pointer"
                  disabled={loading}
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">Set Custom Password</div>
                  <div className="text-xs text-gray-600 mt-1">
                    You provide a password, and user will receive an email with credentials.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Custom Password Field */}
          {useCustomPassword && (
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  {...register('password', {
                    required: useCustomPassword ? 'Password is required' : false,
                    minLength: useCustomPassword ? { value: 8, message: 'Password must be at least 8 characters' } : undefined
                  })}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Password must be at least 8 characters long.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userId: string, userData: UpdateUserData) => void;
  user: User | null;
  loading: boolean;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  loading
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm<UpdateUserFormData>({
    resolver: zodResolver(updateUserSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      roles: [],
      status: 'active'
    }
  });

  const watchedRoles = watch('roles');

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        roles: user.roles,
        status: user.status
      });
    }
  }, [user, reset]);

  const onFormSubmit = (data: UpdateUserFormData) => {
    if (!user) return;
    onSubmit(user.id, data);
  };

  const handleRoleToggle = (roleValue: string) => {
    const currentRoles = watchedRoles || [];
    const newRoles = currentRoles.includes(roleValue)
      ? currentRoles.filter(r => r !== roleValue)
      : [...currentRoles, roleValue];
    setValue('roles', newRoles);
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit User</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-4">
          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={user?.email || ''}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
              disabled
            />
            <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
          </div>

          {/* First Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register('firstName')}
              type="text"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.firstName ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loading}
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register('lastName')}
              type="text"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.lastName ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loading}
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>
            )}
          </div>

          {/* Roles */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Roles <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {USER_ROLES.map((role) => (
                <label key={role.value} className="flex items-start">
                  <input
                    type="checkbox"
                    checked={watchedRoles?.includes(role.value) || false}
                    onChange={() => handleRoleToggle(role.value)}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    disabled={loading}
                  />
                  <div className="ml-2">
                    <div className="text-sm font-medium text-gray-900">{role.label}</div>
                    <div className="text-xs text-gray-500">{role.description}</div>
                  </div>
                </label>
              ))}
            </div>
            {errors.roles && (
              <p className="text-red-500 text-sm mt-1">{errors.roles.message}</p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              {...register('status')}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.status ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loading}
            >
              {USER_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            {errors.status && (
              <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DeleteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: string) => void;
  user: User | null;
  loading: boolean;
}

export const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  user,
  loading
}) => {
  const handleConfirm = () => {
    if (user) {
      onConfirm(user.id);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Delete User</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-gray-600">
              Are you sure you want to delete <strong>{user.firstName} {user.lastName}</strong>?
            </p>
            <p className="text-gray-600 mt-2">
              This action cannot be undone. The user will lose access to their account and all associated data.
            </p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <AlertTriangle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-800">Warning</h3>
                <p className="text-sm text-red-700 mt-1">
                  This will permanently delete the user account and cannot be reversed.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete User'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Password Change Modal
interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

const changePasswordSchema = z.object({
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(1, 'Please confirm the password')
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  user
}) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: ''
    }
  });

  const handleClose = () => {
    reset();
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  const onSubmit = async (data: ChangePasswordFormData) => {
    if (!user) return;

    setLoading(true);
    try {
      await api.patch(`/firm-admin/users/${user.id}/password`, {
        newPassword: data.newPassword
      });

      toast.success('Password updated successfully');
      handleClose();
    } catch (error: any) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Key className="w-5 h-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm text-blue-800">
              Changing password for: <strong>{user.firstName} {user.lastName}</strong> ({user.email})
            </p>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                {...register('newPassword')}
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.newPassword ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={loading}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.newPassword && (
              <p className="text-red-500 text-sm mt-1">{errors.newPassword.message}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                {...register('confirmPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={loading}
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// User Import Modal Component
interface UserImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (users: CreateUserData[]) => void;
  isLoading?: boolean;
}

// Import Results Modal Component
interface ImportResult {
  successful: any[];
  failed: Array<{ user: CreateUserData; error: string }>;
  total: number;
  isImporting?: boolean;
  currentProgress?: number;
}

interface UserImportResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: ImportResult | null;
}

export const UserImportResultsModal: React.FC<UserImportResultsModalProps> = ({
  isOpen,
  onClose,
  results,
}) => {
  if (!isOpen || !results) return null;

  const successCount = results.successful.length;
  const failureCount = results.failed.length;
  const isImporting = results.isImporting || false;
  const progress = results.currentProgress || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Import Results</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Progress Indicator */}
          {isImporting && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-blue-900">Importing Users...</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Processing {results.total} users. Please wait...
                  </p>
                </div>
                <div className="text-2xl font-bold text-blue-600">{progress}%</div>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-blue-600 h-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="mt-3 text-sm text-blue-700">
                {successCount} successful, {failureCount} failed
              </div>
            </div>
          )}

          {/* Summary Cards */}
          {!isImporting && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900">Total Records</p>
                  <p className="text-2xl font-bold text-blue-600">{results.total}</p>
                </div>
                <Upload className="w-8 h-8 text-blue-400" />
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-900">Successfully Imported</p>
                  <p className="text-2xl font-bold text-green-600">{successCount}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-900">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{failureCount}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </div>
          </div>
          )}

          {/* Failed Imports */}
          {!isImporting && failureCount > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start mb-4">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2" />
                <h3 className="text-lg font-medium text-red-900">Failed Imports ({failureCount})</h3>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-red-200">
                  <thead className="bg-red-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-900 uppercase">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-900 uppercase">First Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-900 uppercase">Last Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-900 uppercase">Reason for Failure</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-red-100">
                    {results.failed.map((item, index) => (
                      <tr key={index} className="hover:bg-red-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{item.user.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.user.firstName}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.user.lastName}</td>
                        <td className="px-4 py-3 text-sm text-red-600">{item.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Success Message */}
          {!isImporting && successCount > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-2" />
                <div>
                  <h3 className="text-lg font-medium text-green-900">Successfully Imported</h3>
                  <p className="text-sm text-green-700 mt-1">
                    {successCount} user{successCount !== 1 ? 's' : ''} {successCount !== 1 ? 'have' : 'has'} been successfully imported.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            disabled={isImporting}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isImporting ? 'Importing...' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const UserImportModal: React.FC<UserImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  isLoading = false,
}) => {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing'>('upload');
  const [parsedData, setParsedData] = useState<CreateUserData[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setStep('upload');
      setParsedData([]);
      setErrors([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isLoading) {
      setStep('importing');
    }
  }, [isLoading]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      parseCSVFile(file);
    }
  };

  const parseCSVFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      try {
        const parsed = parseCSV(text);
        setParsedData(parsed.data);
        setErrors(parsed.errors);
        setStep('preview');
      } catch (error) {
        setErrors(['Failed to parse CSV file. Please check the format.']);
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (text: string): { data: CreateUserData[]; errors: string[] } => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
    const data: CreateUserData[] = [];
    const errors: string[] = [];

    // Expected headers mapping
    const headerMap: Record<string, string> = {
      'email': 'email',
      'first name': 'firstName',
      'last name': 'lastName',
      'roles': 'roles',
      'password': 'password'
    };

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());

      if (values.length !== headers.length) {
        errors.push(`Row ${i + 1}: Column count mismatch`);
        continue;
      }

      const user: CreateUserData = {
        email: '',
        firstName: '',
        lastName: '',
        roles: [],
        sendInvitation: false,
      };

      let hasEmail = false;
      let hasFirstName = false;
      let hasLastName = false;
      let hasPassword = false;

      headers.forEach((header, index) => {
        const mappedField = headerMap[header];
        const value = values[index];

        if (!mappedField || !value) return;

        if (mappedField === 'email') {
          user.email = value;
          hasEmail = true;
        } else if (mappedField === 'firstName') {
          user.firstName = value;
          hasFirstName = true;
        } else if (mappedField === 'lastName') {
          user.lastName = value;
          hasLastName = true;
        } else if (mappedField === 'roles') {
          // Parse roles - can be comma-separated or single role
          const rolesList = value.split(';').map(r => r.trim()).filter(r => r);
          const validRoles = ['Manager', 'Staff', 'Client'];
          user.roles = rolesList.filter(r => validRoles.includes(r));
          if (user.roles.length === 0) {
            user.roles = ['Staff']; // Default role
          }
        } else if (mappedField === 'password') {
          user.password = value;
          hasPassword = true;
        }
      });

      if (!hasEmail) {
        errors.push(`Row ${i + 1}: Email is required`);
        continue;
      }

      if (!hasFirstName) {
        errors.push(`Row ${i + 1}: First name is required`);
        continue;
      }

      if (!hasLastName) {
        errors.push(`Row ${i + 1}: Last name is required`);
        continue;
      }

      if (!hasPassword) {
        errors.push(`Row ${i + 1}: Password is required`);
        continue;
      }

      data.push(user);
    }

    return { data, errors };
  };

  const handleImport = () => {
    if (parsedData.length > 0) {
      setStep('importing');
      onImport(parsedData);
    }
  };

  const downloadTemplate = () => {
    const template = [
      'Email,First Name,Last Name,Roles,Password',
      'john.doe@example.com,John,Doe,Staff,TempPass123!',
      'jane.smith@example.com,Jane,Smith,Manager,SecurePass456!',
      'client@example.com,Client,User,Client,ClientPass789!'
    ].join('\n');

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'user-import-template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Import Users</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Upload CSV File</h3>
                <p className="text-gray-600 mb-4">
                  Upload a CSV file with user data. Make sure your file includes the required columns.
                </p>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  id="csv-upload-users"
                />
                <label
                  htmlFor="csv-upload-users"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm font-medium text-gray-900">Click to upload CSV file</span>
                  <span className="text-xs text-gray-500">or drag and drop</span>
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-2" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">CSV Format Requirements</h4>
                    <ul className="text-sm text-blue-800 mt-1 list-disc list-inside">
                      <li>First row must contain column headers</li>
                      <li>Email, First Name, Last Name, and Password columns are required</li>
                      <li>Roles must be: Manager, Staff, or Client (separate multiple roles with semicolon)</li>
                      <li>Password will be sent to the user via email along with login credentials</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={downloadTemplate}
                  className="flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Preview Import Data</h3>
                <p className="text-gray-600">
                  Review the data below before importing. {parsedData.length} user(s) will be imported.
                </p>
              </div>

              {errors.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-900">Warnings</h4>
                      <ul className="text-sm text-yellow-800 mt-1 list-disc list-inside">
                        {errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">First Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Roles</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Password</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {parsedData.map((user, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{user.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{user.firstName}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{user.lastName}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{user.roles.join(', ')}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono">{'•'.repeat(user.password?.length || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setStep('upload')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600"
                  disabled={parsedData.length === 0}
                >
                  Import {parsedData.length} User(s)
                </button>
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Importing Users...</h3>
              <p className="text-gray-600">Please wait while we import your users.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
